/* eslint-disable react/jsx-no-target-blank */
import useRequest from '@ahooksjs/use-request';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import { FaFilePdf } from 'react-icons/fa';

import { uiHelpers } from '../../helpers';
import { eligibleForHKEXProfile } from '../../helpers/stockCode.helper';
import { ccassService } from '../../services';
import LoadingImage from '../general/LoadingImage';

interface Props {
  stockCode: string;
  language: string;
  onClickNews: (news: any) => void;
  useHyperlink?: boolean;
}
const StockNews: React.FC<Props> = ({
  stockCode,
  language,
  onClickNews,
  useHyperlink,
}) => {
  const [newsList, setNewsList] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const { data, loading, run } = useRequest(ccassService.stockNewsOptions, {
    manual: true,
  });

  const load = useCallback(() => {
    if (eligibleForHKEXProfile(stockCode)) {
      run(
        `hk.${Number.parseInt(stockCode)}`,
        language,
        newsList.length > 0 ? newsList.at(-1).NewsId : null
      );
    }
  }, [language, newsList, run, stockCode]);

  useEffect(() => {
    if (data?.length > 0) {
      setNewsList(list => {
        // make sure there are no duplicates
        if (
          list.every(
            ({ NewsId }) => !data?.some(item => item.NewsId === NewsId)
          )
        ) {
          return [...list, ...data];
        }
        return list;
      });
    }
  }, [data]);

  const noMoreNews = useMemo(() => data?.length === 0, [data?.length]);

  useEffect(() => {
    if (firstLoad) {
      load();
      setFirstLoad(false);
    }
  }, [firstLoad, load]);

  const { t } = useTranslation();

  if (!loading && newsList.length === 0) return <p>{t('No data available')}</p>;
  return (
    <>
      <Row>
        <Col md={12}>
          <Table bordered size="sm" style={{ fontSize: '0.8rem' }}>
            <tbody>
              <tr>
                <th>{t('release-time')}</th>
                <td>{t('content')}</td>
              </tr>
              {newsList.map(n => {
                // if news is an htm or html file, open in a new tab
                // https://app.clubhouse.io/datalouder/story/54/link-in-news-inside-hkexnews-not-working
                const forceLink =
                  n.ext.endsWith('.htm') || n.ext.endsWith('.html');

                const simplifiedNews = {
                  title: n.title,
                  id: n.NewsId,
                };
                return (
                  <tr key={n.NewsId}>
                    <td>
                      {uiHelpers.formatDateTimeLocal(
                        new Date(n.ReleaseTime * 1000)
                      )}
                    </td>
                    <td>
                      <strong>{n.sTxt}</strong>{' '}
                      {useHyperlink || forceLink ? (
                        <a
                          href={`/news/${n.NewsId}`}
                          target="_blank"
                          onClick={() => onClickNews(simplifiedNews)}
                        >
                          <span>{n.title}</span>
                        </a>
                      ) : (
                        <u
                          className="clickable"
                          onClick={() => onClickNews(simplifiedNews)}
                        >
                          <span>{n.title}</span>
                        </u>
                      )}{' '}
                      <Badge bg="secondary">{n.size}</Badge>{' '}
                      {n.ext === 'pdf' ? (
                        <FaFilePdf />
                      ) : (
                        <Badge bg="info">{n.ext}</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <LoadingImage loading={loading} displayAsBlock={true} />
        </Col>
      </Row>
      <Row>
        <Col className="text-center">
          <small
            style={{
              fontSize: '0.8rem',
              color: 'gray',
            }}
          >
            {t('scanned-5-min')}{' '}
            {!noMoreNews && (
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  load();
                }}
              >
                {t('load-more')}
              </a>
            )}
          </small>
        </Col>
      </Row>
    </>
  );
};

export default StockNews;
